import i18n from '@web/plugins/i18n'

import {
  MenuState,
  ItemMenu,
  Menu,
  MenuGettersTree
} from '@web/store/types/modules/menu'
import {
  isVideoConsultationSupport,
  guidelineUrl,
  instructionUrl,
  communityUrlFrontend
} from '@web/common/ExternalConfig/getters'
import { getUrlWithAuthParamsCommunity } from '@web/common/Utils'
import PAGE_NAME from '@web/consts/PAGE_NAME'

const state: MenuState = {
  home: {
    icon: '-course',
    name: String(i18n.global.t('myCourses')),
    link: {
      name: PAGE_NAME.HOME
    },
    note: '',
    type: 'router-link'
  },
  course: {
    icon: '-course',
    name: String(i18n.global.t('myCourses')),
    link: {
      name: 'courses'
    },
    note: '',
    type: 'router-link'
  },
  chat: {
    icon: '-chat',
    name: String(i18n.global.t('messages')),
    link: {
      name: 'chats'
    },
    note: '',
    type: 'router-link'
  },
  homework: {
    icon: '-homework',
    name: String(i18n.global.t('homeworks')),
    link: {
      name: 'homeworks'
    },
    note: '',
    type: 'router-link'
  },
  videoConsultations: {
    icon: '-videoconsultations',
    name: String(i18n.global.t('videoConsultations.VideoConsultations')),
    link: {
      name: 'videoconsultations'
    },
    note: '',
    type: 'router-link'
  },
  support: {
    icon: '-support',
    name: String(i18n.global.t('supportCenter')),
    link: { name: 'support' },
    note: '',
    type: 'router-link'
  },
  edit: {
    icon: '-userEdit',
    name: String(i18n.global.t('editProfile')),
    link: {
      name: 'userEdit'
    },
    note: '',
    type: 'router-link'
  },
  socialNetworks: {
    icon: '-link',
    name: String(i18n.global.t('linkSocialNetworks')),
    link: {
      name: 'socialNetworks'
    },
    note: '',
    type: 'router-link'
  },
  settings: {
    icon: '-settings',
    name: String(i18n.global.t('Settings')),
    link: {
      name: 'userSettings'
    },
    note: '',
    type: 'router-link'
  },
  password: {
    icon: '-password',
    name: String(i18n.global.t('password')),
    link: {
      name: 'userPassword'
    },
    note: '',
    type: 'router-link'
  },
  catalog: {
    icon: '-catalog',
    name: String(i18n.global.t('catalog')),
    link: {
      name: 'catalog'
    },
    note: '',
    type: 'router-link'
  },
  points: {
    icon: '-points',
    name: String(i18n.global.t('Points')),
    link: {
      name: 'points'
    },
    type: 'router-link'
  },
  deleteMenu: {
    icon: '-delete',
    name: String(i18n.global.t('Delete')),
    link: {
      name: 'delete'
    },
    type: 'router-link'
  },
  exit: {
    icon: '-exit',
    name: String(i18n.global.t('accountExit')),
    link: {
      name: 'logout'
    },
    type: 'router-link'
  },
  subscriptions: {
    icon: '-subscriptions',
    name: String(i18n.global.t('Subscriptions')),
    link: {
      name: 'subscriptions'
    },
    type: 'router-link'
  }
}

const getters: MenuGettersTree = {
  getSupport ({ support }, _1, _2, rootGetters) {
    return {
      ...support,
      name: rootGetters['user/getShowNewLk'] ? String(i18n.global.t('faq')) : String(i18n.global.t('supportCenter'))
    }
  },
  getVideoConsultations ({ videoConsultations }, _1, _2, rootGetters) {
    return rootGetters['user/getShowNewLk'] ? undefined : videoConsultations
  },
  getEditProfile ({ edit }, _1, _2, rootGetters) {
    return rootGetters['user/getShowNewLk'] ? undefined : edit
  },
  getPoints ({ points }, _1, _2, rootGetters) {
    return rootGetters['user/getShowNewLk'] ? undefined : points
  },
  getSocialNetworks ({ socialNetworks }, _1, _2, rootGetters) {
    return rootGetters['user/getShowNewLk'] ? undefined : socialNetworks
  },
  general ({ support, home, homework, chat, subscriptions }, { getVideoConsultations }, _, rootGetters) {
    const menu: Menu = {
      home,
      community: {
        icon: '-community',
        name: String(i18n.global.t('Community')),
        link: getUrlWithAuthParamsCommunity(communityUrlFrontend()),
        type: 'club'
      },
      homework,
      chat,
      videoConsultations: getVideoConsultations,
      support,
      subscriptions
    }

    const communities = rootGetters['community/communities']
      .filter(community => community.userCommunity)
      .filter(community => !community.chatOnly)

    if (!communityUrlFrontend() || !communities.length) {
      delete menu.community
    }

    for (const key in menu) {
      if (!menu[key]) {
        delete menu[key]
      }
    }

    return menu
  },

  setting ({ settings, exit }, { getSupport, getEditProfile, getPoints, getSocialNetworks }, _, rootGetters) {
    const menu: Menu = {
      support: getSupport,
      edit: getEditProfile,
      settings,
      points: getPoints
    }

    if (guidelineUrl()) {
      menu.guideline = {
        icon: '-manual',
        name: String(i18n.global.t('UserManual')),
        link: guidelineUrl(),
        type: 'modal'
      }
    }

    if (instructionUrl()) {
      menu.instruction = {
        icon: '-instruction',
        name: String(i18n.global.t('instruction')),
        link: instructionUrl(),
        type: 'modal'
      }
    }

    if (rootGetters['assets/authCollection'].length && getSocialNetworks) {
      menu.socialNetworks = getSocialNetworks
    }

    menu.exit = exit

    for (const key in menu) {
      if (!menu[key]) {
        delete menu[key]
      }
    }

    return menu
  },

  mobile (_, { getSupport, general, setting }) {
    return {
      support: getSupport,
      ...general,
      ...setting
    }
  },
  mobileProfile ({ settings, catalog, deleteMenu }, { getSupport, getVideoConsultations, getEditProfile, getSocialNetworks }, _, rootGetters) {
    const menu: Menu = {
      edit: getEditProfile
        ? {
            ...getEditProfile,
            name: String(i18n.global.t('myInformation'))
          }
        : undefined,
      socialNetworks: getSocialNetworks,
      settings,
      videoConsultations: getVideoConsultations,
      support: getSupport,
      catalog,
      deleteMenu
    }

    for (const key in menu) {
      if (!menu[key]) {
        delete menu[key]
      }
    }

    if (!rootGetters['assets/authCollection'].length) {
      delete menu.socialNetworks
    }

    if (isVideoConsultationSupport()) {
      delete menu.videoConsultations
    }

    return menu
  }
}

export default {
  namespaced: true,
  state,
  getters
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any
